/**
 * Google Meet-style always-on-top call widget via the Document Picture-in-Picture
 * API (Chrome/Edge 116+, desktop only). The PiP window belongs to the tab that owns
 * the call but stays visible over every tab and application. Built with plain DOM —
 * no React/Chakra in the detached document — and fed by polling callbacks (peer
 * stream, mic state, duration), so it needs no store wiring.
 */

type RequestPipWindow = (options?: { width?: number; height?: number }) => Promise<Window>;

type DocumentPip = { requestWindow: RequestPipWindow };

export type CallPipHandle = { close: () => void };

export type CallPipBinding = { source: string; mirror: boolean };

export type CallPipOptions = {
  peerName: string;
  peerAvatarUrl: string | null;
  /**
   * Binds the spotlight track to the widget's <video> and reports what got bound
   * (null — nothing live, show the avatar). Called on every poll tick: the real
   * LiveKit track is attached here, not a copied srcObject, so adaptive stream
   * keeps the stream alive while the owner tab sits in the background.
   */
  attachVideo: (element: HTMLVideoElement) => CallPipBinding | null;
  /** Unbind the track before the window is torn down. */
  detachVideo: (element: HTMLVideoElement) => void;
  getDurationLabel: () => string;
  isMicEnabled: () => boolean;
  onToggleMic: () => void;
  onHangup: () => void;
  /** Bring the owner tab back to front (PiP closes itself first). */
  onExpand: () => void;
  onClosed: () => void;
  labels: {
    mic: string;
    hangup: string;
    expand: string;
    /** Caption per spotlight source — the widget shows one stream at a time. */
    sources: Record<string, string>;
  };
};

const getDocumentPip = (): DocumentPip | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const candidate = (window as Window & { documentPictureInPicture?: DocumentPip })
    .documentPictureInPicture;
  return candidate ?? null;
};

export const isDocumentPipSupported = (): boolean => getDocumentPip() !== null;

const PIP_CSS = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { width: 100%; height: 100%; }
  body {
    background: #12121d; color: #fff; overflow: hidden;
    font-family: system-ui, -apple-system, sans-serif;
    display: flex; flex-direction: column;
  }
  .stage { position: relative; flex: 1; min-height: 0; background: #000; }
  .stage video { width: 100%; height: 100%; object-fit: contain; }
  .idle { position: absolute; inset: 0; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 8px; background: #1d1d31; }
  .idle img { width: 56px; height: 56px; border-radius: 50%; object-fit: cover; }
  .idle .initial { width: 56px; height: 56px; border-radius: 50%; background: #4b50e6;
    display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: 700; }
  .idle .name { font-size: 13px; color: rgba(255,255,255,0.85); }
  .source { position: absolute; left: 6px; bottom: 6px; max-width: calc(100% - 12px);
    padding: 2px 8px; border-radius: 999px; background: rgba(0,0,0,0.6);
    font-size: 10px; color: rgba(255,255,255,0.9); pointer-events: none;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .bar { display: flex; align-items: center; gap: 8px; padding: 8px 10px; background: #12121d; }
  .timer { font-size: 12px; color: #2bd576; font-variant-numeric: tabular-nums; flex: 1; }
  button { border: 0; border-radius: 10px; width: 34px; height: 30px; cursor: pointer;
    display: flex; align-items: center; justify-content: center; font-size: 14px;
    background: #272743; color: #fff; }
  button:hover { filter: brightness(1.2); }
  button.muted { background: #f23f43; }
  button.hangup { background: #f23f43; }
`;

/** Opens the PiP window; resolves null when the browser refuses (no support / no gesture). */
export const openCallPipWindow = async (options: CallPipOptions): Promise<CallPipHandle | null> => {
  const pipApi = getDocumentPip();
  if (!pipApi) {
    return null;
  }

  let pip: Window;
  try {
    pip = await pipApi.requestWindow({ width: 320, height: 240 });
  } catch {
    return null; // needs a user gesture or PiP is blocked
  }

  const doc = pip.document;
  const style = doc.createElement('style');
  style.textContent = PIP_CSS;
  doc.head.append(style);
  doc.title = options.peerName;

  const stage = doc.createElement('div');
  stage.className = 'stage';
  const video = doc.createElement('video');
  video.autoplay = true;
  video.muted = true; // audio keeps playing in the owner tab
  ;(video as HTMLVideoElement & { playsInline: boolean }).playsInline = true;

  const idle = doc.createElement('div');
  idle.className = 'idle';
  if (options.peerAvatarUrl) {
    const img = doc.createElement('img');
    img.src = options.peerAvatarUrl;
    img.alt = '';
    idle.append(img);
  } else {
    const initial = doc.createElement('div');
    initial.className = 'initial';
    initial.textContent = (options.peerName[0] ?? '?').toUpperCase();
    idle.append(initial);
  }
  const name = doc.createElement('div');
  name.className = 'name';
  name.textContent = options.peerName;
  idle.append(name);
  const sourceLabel = doc.createElement('div');
  sourceLabel.className = 'source';
  stage.append(video, idle, sourceLabel);

  const bar = doc.createElement('div');
  bar.className = 'bar';
  const timer = doc.createElement('div');
  timer.className = 'timer';
  const micButton = doc.createElement('button');
  micButton.title = options.labels.mic;
  micButton.textContent = '🎙';
  micButton.addEventListener('click', options.onToggleMic);
  const expandButton = doc.createElement('button');
  expandButton.title = options.labels.expand;
  expandButton.textContent = '⤢';
  expandButton.addEventListener('click', () => {
    pip.close();
    options.onExpand();
  });
  const hangupButton = doc.createElement('button');
  hangupButton.className = 'hangup';
  hangupButton.title = options.labels.hangup;
  hangupButton.textContent = '✕';
  hangupButton.addEventListener('click', () => {
    options.onHangup();
    pip.close();
  });
  bar.append(timer, micButton, expandButton, hangupButton);
  doc.body.append(stage, bar);

  const sync = () => {
    timer.textContent = options.getDurationLabel();
    micButton.className = options.isMicEnabled() ? '' : 'muted';
    micButton.textContent = options.isMicEnabled() ? '🎙' : '🔇';
    const binding = options.attachVideo(video);
    const hasVideo = binding !== null;
    // Own camera reads as a mirror, everything else (screens, peer camera) as-is.
    video.style.transform = binding?.mirror ? 'scaleX(-1)' : 'none';
    // Same rule as the in-app stage: a shared screen must stay readable edge to
    // edge, a camera looks better cropped to fill.
    video.style.objectFit = binding?.source.includes('Screen') ? 'contain' : 'cover';
    idle.style.display = hasVideo ? 'none' : 'flex';
    video.style.display = hasVideo ? 'block' : 'none';
    sourceLabel.textContent = binding ? (options.labels.sources[binding.source] ?? '') : '';
    sourceLabel.style.display = sourceLabel.textContent ? 'block' : 'none';
    if (hasVideo && video.paused) {
      video.play().catch(() => undefined);
    }
  };
  sync();
  const interval = pip.setInterval(sync, 500);

  const teardown = () => {
    pip.clearInterval(interval);
    options.detachVideo(video);
  };

  const handle: CallPipHandle = {
    close: () => {
      teardown();
      pip.close();
    },
  };

  pip.addEventListener('pagehide', () => {
    teardown();
    options.onClosed();
  });

  return handle;
};
